import { isEmpty } from 'lodash';
import { MESSAGE_TYPE_JSON, MessageType } from 'api/MessageTypeAPI';
import { FETCH_CHANNELS_URL, Channel } from 'api/ChannelsAPI';
import {
  MessagingType,
  UnscoredMetrics,
  UnsubMessage,
  UnsubMessageEnhanced,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import {
  UNSCORED_UNSUBSCRIBE_TITLE,
  UNSCORED_UNSUBSCRIBE_TOOLTIP_NOTICE,
  UNSCORED_UNSUBSCRIBES,
} from 'modules/intelligentInsights/brandAffinityInsights/constants';
import {
  LeftMessagingContainerFlex,
  MainMessagingInfoFlex,
  RightMessagingContainerFlex,
  SecondaryCaption,
  SpacingOffsetCount,
  SpacingOffsetIndex,
  UnscoredUnsubsubcribeMetricsCard,
  UnscoredUnsubsubcribeMetricsContainer,
  UnsubscribeTitle,
  UnsubscribeTopContainer,
} from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/UnscoredUnsubsubcribeMetrics/UnscoredUnsubsubcribeMetrics.styles';
import { UnscoredUnsubsubcribeMetricsSkeleton } from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/UnscoredUnsubsubcribeMetrics/UnscoredUnsubsubcribeMetricsSkeleton';
import { UnscoredUnsubsubcribeMetricsError } from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/UnscoredUnsubsubcribeMetrics/UnscoredUnsubsubcribeMetricsError';
import { FC, Fragment, useEffect, useState } from 'react';
import { useRestQuery } from 'utils/hooks/api';
import { isUndefined } from 'utils/tsHelpers';
import { MarginlessBody2Bold } from 'modules/intelligentInsights/brandAffinityInsights/components/common.styles';
import { HelpToggletip, TipPlacement } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { formatCount } from 'modules/intelligentInsights/domain.utils';

export const getTopMessagingTypes = (
  topMessageTypes: Array<UnsubMessage>,
  topChannels: Array<UnsubMessage>,
) => {
  if (isEmpty(topMessageTypes) && isEmpty(topChannels)) {
    return [] as Array<UnsubMessage>;
  }
  const messageTypes = topMessageTypes.map((mt) => ({ ...mt, type: MessagingType.MessageType }));
  const channels = topChannels.map((ch) => ({ ...ch, type: MessagingType.Channel }));

  return messageTypes
    .concat(channels)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
};

export const getEnhancedMessagingData = (
  topMessagingTypes: Array<UnsubMessage>,
  messagesData: Array<MessageType>,
  channelData: Array<Channel>,
) => {
  return topMessagingTypes.map((topMessageType) => {
    let name = '';
    if (topMessageType.type === MessagingType.Channel) {
      name = channelData.find((messageType) => messageType.id === topMessageType.id)?.name || '';
    } else {
      name = messagesData.find((messageType) => messageType.id === topMessageType.id)?.name || '';
    }

    return {
      ...topMessageType,
      name,
      type: topMessageType.type as MessagingType,
    };
  });
};

export const UnscoredUnsubsubcribeMetricsTestId = 'unscored-unsubsubcribe-metrics';
export const UnscoredUnsubsubcribeMetricsLeftBlock = 'unscored-unsubsubcribe-metrics-left-block';
export const UnscoredUnsubsubcribeMetricsRightBlock = 'unscored-unsubsubcribe-metrics-right-block';

export interface UnscoredUnsubsubcribeMetricsProps {
  unscoredMetrics: UnscoredMetrics;
}
export const UnscoredUnsubsubcribeMetrics: FC<UnscoredUnsubsubcribeMetricsProps> = ({
  unscoredMetrics,
}) => {
  const [collectedMessagingTypes, setCollectedMessagingTypes] = useState<
    Array<UnsubMessageEnhanced>
  >([]);
  const { topUnsubChannelIds, topUnsubMessageTypeIds } = unscoredMetrics;
  const {
    data: messagesData,
    isError: isMessageTypesError,
    isLoading: isMessageTypesLoading,
    refetch: refetchMessages,
  } = useRestQuery<Array<MessageType>>(MESSAGE_TYPE_JSON, { enabled: false });
  const {
    data: channelData,
    isError: isChannelTypesError,
    isLoading: isChannelTypesLoading,
    refetch: refetchChannels,
  } = useRestQuery<Array<Channel>>(FETCH_CHANNELS_URL, { enable: false });

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    refetchMessages();
    refetchChannels();
  }, []);

  useEffect(() => {
    if (isUndefined(messagesData) || isUndefined(channelData)) {
      return;
    }

    const topMessagingTypes = getTopMessagingTypes(topUnsubMessageTypeIds, topUnsubChannelIds);
    const matchedMessageTypes = getEnhancedMessagingData(
      topMessagingTypes,
      messagesData,
      channelData,
    );
    setCollectedMessagingTypes(matchedMessageTypes);
  }, [messagesData, channelData, topUnsubMessageTypeIds, topUnsubChannelIds]);

  if (isEmpty(topUnsubChannelIds) && isEmpty(topUnsubMessageTypeIds)) {
    return null;
  }

  if (isMessageTypesLoading || isChannelTypesLoading) {
    return <UnscoredUnsubsubcribeMetricsSkeleton />;
  }

  if (isMessageTypesError || isChannelTypesError) {
    return (
      <UnscoredUnsubsubcribeMetricsError
        onRefresh={() => {
          refetchMessages();
          refetchChannels();
        }}
      />
    );
  }

  return (
    <UnscoredUnsubsubcribeMetricsCard data-test={UnscoredUnsubsubcribeMetricsTestId}>
      <UnsubscribeTopContainer>
        <UnsubscribeTitle>{UNSCORED_UNSUBSCRIBE_TITLE}</UnsubscribeTitle>
        <HelpToggletip
          iconProps={{ color: colors.secondaryText }}
          tooltipContent={UNSCORED_UNSUBSCRIBE_TOOLTIP_NOTICE}
          placement={TipPlacement.Top}
        />
      </UnsubscribeTopContainer>
      <UnscoredUnsubsubcribeMetricsContainer>
        {collectedMessagingTypes.map((messagingItem, index) => {
          return (
            <Fragment key={`${messagingItem.id}.${messagingItem.name}`}>
              <LeftMessagingContainerFlex data-test={UnscoredUnsubsubcribeMetricsLeftBlock}>
                <MainMessagingInfoFlex>
                  <MarginlessBody2Bold>
                    <SpacingOffsetIndex>{index + 1}.</SpacingOffsetIndex>
                    {messagingItem.name}
                  </MarginlessBody2Bold>
                  <SpacingOffsetCount>
                    {formatCount(messagingItem.score)} {UNSCORED_UNSUBSCRIBES}
                  </SpacingOffsetCount>
                </MainMessagingInfoFlex>
              </LeftMessagingContainerFlex>
              <RightMessagingContainerFlex data-test={UnscoredUnsubsubcribeMetricsRightBlock}>
                <SecondaryCaption>{messagingItem.type}</SecondaryCaption>
                <SecondaryCaption>&bull;</SecondaryCaption>
                <SecondaryCaption> ID {messagingItem.id}</SecondaryCaption>
              </RightMessagingContainerFlex>
            </Fragment>
          );
        })}
      </UnscoredUnsubsubcribeMetricsContainer>
    </UnscoredUnsubsubcribeMetricsCard>
  );
};
